import { useEffect, useRef, useState } from "react";
import { message, Modal } from "antd";
import { useTranslation } from "react-i18next";
import { s3ImageLink } from "@/utils/links";
import { PaymentProvider, SavedPaymentMethod } from "./types";
import { formatCardBrand } from "./cardBrand";

const CARD_BRAND_IMAGES: Record<string, string> = {
  visa: "https://js.stripe.com/v3/fingerprinted/img/visa-729c05c240c4bdb47b03ac81d9945bfe.svg",
  mastercard:
    "https://js.stripe.com/v3/fingerprinted/img/mastercard-4d8844094130711885b5e41b28c9848f.svg",
  amex: "https://js.stripe.com/v3/fingerprinted/img/amex-a49b82f46c5cd6a96a6e418a6ca1717c.svg",
  discover:
    "https://js.stripe.com/v3/fingerprinted/img/discover-ac52cd46f89fa40a29a0bfb954e33173.svg",
  diners:
    "https://js.stripe.com/v3/fingerprinted/img/diners-fbcbd3360f8e3f629cdaa80e93abdb8b.svg",
  jcb: "https://js.stripe.com/v3/fingerprinted/img/jcb-271fd06e6e7a2c52692ffa91a95fb64f.svg",
  unionpay:
    "https://js.stripe.com/v3/fingerprinted/img/unionpay-8a10aefc7295216c338ba4e1224627a1.svg",
};

interface Props {
  open: boolean;
  methods?: SavedPaymentMethod[];
  methodsByProvider?: Partial<Record<PaymentProvider, SavedPaymentMethod[]>>;
  onClose: () => void;
  onAdd: (provider?: PaymentProvider) => void;
  onRemove?: (id: string, provider?: PaymentProvider) => void | Promise<void>;
  onSetDefault?: (
    id: string,
    provider?: PaymentProvider
  ) => void | Promise<void>;
}

function CardBrandIcon({ brand }: { brand: string }) {
  const image = CARD_BRAND_IMAGES[brand.toLowerCase()];

  return (
    <span className="flex h-7 w-10 flex-none items-center justify-center rounded border border-[#e7e7e7] bg-white p-1">
      <img
        className="max-h-full max-w-full"
        src={image || `${s3ImageLink}credit-card.svg`}
        alt={`${brand} card`}
        onError={(event) => {
          event.currentTarget.src = `${s3ImageLink}credit-card.svg`;
        }}
      />
    </span>
  );
}

const getErrorMessage = (error: any, fallback: string) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  fallback;

export default function ManagePaymentMethodsModal({
  open,
  methods = [],
  methodsByProvider,
  onClose,
  onAdd,
  onRemove,
  onSetDefault,
}: Props) {
  const { t } = useTranslation();
  const hasProviderTabs = Boolean(methodsByProvider);
  const [provider, setProvider] = useState<PaymentProvider>("airwallex");
  const [pendingRemoval, setPendingRemoval] = useState<{
    method: SavedPaymentMethod;
    provider: PaymentProvider;
  }>();
  const [isRemoving, setIsRemoving] = useState(false);
  const [settingDefaultId, setSettingDefaultId] = useState<string>();
  const hasInitializedProviderRef = useRef(false);
  const isUpdatingPaymentMethod = isRemoving || Boolean(settingDefaultId);

  useEffect(() => {
    if (!open) {
      hasInitializedProviderRef.current = false;
      return;
    }
    if (!hasProviderTabs || hasInitializedProviderRef.current) return;

    setProvider(
      methodsByProvider?.airwallex?.length ? "airwallex" : "stripe"
    );
    hasInitializedProviderRef.current = true;
  }, [hasProviderTabs, methodsByProvider?.airwallex?.length, open]);

  const activeMethods = hasProviderTabs
    ? methodsByProvider?.[provider] || []
    : methods;

  const handleClose = () => {
    if (isUpdatingPaymentMethod) return;
    setPendingRemoval(undefined);
    onClose();
  };

  const handleRemove = async () => {
    if (!pendingRemoval || !onRemove || isUpdatingPaymentMethod) return;

    setIsRemoving(true);
    try {
      if (!pendingRemoval.method.paymentMethodId) return;

      await onRemove(
        pendingRemoval.method.paymentMethodId,
        pendingRemoval.method.provider
      );
      setPendingRemoval(undefined);
    } catch (error) {
      message.error(
        getErrorMessage(
          error,
          t("components.chargeModal.managePaymentMethods.removeFailed", {
            defaultValue: "Unable to remove this payment method. Please try again.",
          })
        )
      );
    } finally {
      setIsRemoving(false);
    }
  };

  const handleSetDefault = async (method: SavedPaymentMethod) => {
    if (!onSetDefault || !method.paymentMethodId || isUpdatingPaymentMethod) return;

    setSettingDefaultId(method.id);
    try {
      await onSetDefault(method.paymentMethodId, method.provider);
    } catch (error) {
      message.error(
        getErrorMessage(
          error,
          t("components.chargeModal.managePaymentMethods.setDefaultFailed", {
            defaultValue:
              "Unable to set this payment method as default. Please try again.",
          })
        )
      );
    } finally {
      setSettingDefaultId(undefined);
    }
  };

  return (
    <>
      <Modal
        open={open}
        footer={null}
        onCancel={handleClose}
        width={460}
        title={t("components.chargeModal.managePaymentMethods.modalTitle", {
          defaultValue: "Payment method",
        })}
      >
        {hasProviderTabs && (
          <div className="mt-2 flex items-end gap-5 border-b border-[#e7e7e7]">
            {(["airwallex", "stripe"] as PaymentProvider[]).map((item) => {
              const isActive = provider === item;

              return (
                <button
                  key={item}
                  type="button"
                  disabled={isUpdatingPaymentMethod}
                  onClick={() => setProvider(item)}
                  className={`-mb-px flex h-9 items-center border-b-2 px-0 disabled:cursor-not-allowed ${
                    isActive
                      ? "border-[#ff5a1f]"
                      : "border-transparent opacity-60"
                  }`}
                >
                  <img
                    className={`object-contain ${
                      item === "airwallex" ? "h-5 w-[76px]" : "h-5 w-10"
                    }`}
                    src={`${s3ImageLink}${
                      item === "airwallex"
                        ? isActive
                          ? "airwallex-active.png"
                          : "airwallex-default.png"
                        : isActive
                          ? "stripe-active.png"
                          : "stirpe-default.png"
                    }`}
                    alt={item === "airwallex" ? "Airwallex" : "Stripe"}
                  />
                </button>
              );
            })}
          </div>
        )}

        <div className="mt-4">
          <div className="max-h-[312px] space-y-2 overflow-y-auto pr-1">
            {activeMethods.map((method) => (
              <div
                key={method.id}
                className="flex h-[56px] items-center gap-3 rounded-lg border border-[#e1e1e1] px-3 py-2"
              >
                <CardBrandIcon brand={method.brand} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-[#171717]">
                    {t("components.chargeModal.paymentMethodSection.cardEnding", {
                      brand: formatCardBrand(method.brand),
                      last4: method.last4,
                      defaultValue: "{brand} ending {last4}",
                    })}
                  </div>
                  {method.expires && (
                    <div className="text-xs leading-4 text-[#737373]">
                      {t("components.chargeModal.paymentMethodSection.expires", {
                        date: method.expires,
                        defaultValue: "Expires {date}",
                      })}
                    </div>
                  )}
                </div>
                {method.isDefault ? (
                  <span className="rounded-full bg-[#ecfdf3] px-2 py-1 text-xs text-[#027a48]">
                    {t("components.chargeModal.paymentMethodSection.default", {
                      defaultValue: "Default",
                    })}
                  </span>
                ) : onSetDefault && method.paymentMethodId ? (
                  <button
                    className="text-xs text-[#525252] disabled:cursor-not-allowed disabled:opacity-60"
                    type="button"
                    disabled={isUpdatingPaymentMethod}
                    onClick={() => handleSetDefault(method)}
                  >
                    {settingDefaultId === method.id
                      ? t("components.chargeModal.managePaymentMethods.settingDefault", {
                          defaultValue: "Setting…",
                        })
                      : t("components.chargeModal.managePaymentMethods.setDefault", {
                          defaultValue: "Set default",
                        })}
                  </button>
                ) : null}
                {onRemove && method.paymentMethodId && (
                  <button
                    className="text-xs font-medium text-[#d9471a] disabled:cursor-not-allowed disabled:opacity-60"
                    type="button"
                    disabled={isUpdatingPaymentMethod}
                    onClick={() =>
                      setPendingRemoval({ method, provider: method.provider })
                    }
                  >
                    {t("components.chargeModal.managePaymentMethods.remove", {
                      defaultValue: "Remove",
                    })}
                  </button>
                )}
              </div>
            ))}
          </div>
          <button
            type="button"
            disabled={isUpdatingPaymentMethod}
            onClick={() => onAdd(hasProviderTabs ? provider : undefined)}
            className="mt-2 flex h-9 w-full items-center rounded-lg border border-[#e7e7e7] px-3 text-left text-sm text-[#737373] disabled:cursor-not-allowed disabled:opacity-60"
          >
            ＋ {t("components.chargeModal.managePaymentMethods.addPaymentMethod", {
              defaultValue: "Add payment method",
            })}
          </button>
        </div>
      </Modal>

      <Modal
        open={Boolean(pendingRemoval)}
        footer={null}
        closable={false}
        centered
        width={400}
      >
        <h3 className="text-base font-semibold">
          {t("components.chargeModal.managePaymentMethods.removeTitle", {
            defaultValue: "Remove this payment method?",
          })}
        </h3>
        <p className="mt-2 text-xs leading-5 text-[#777]">
          {t("components.chargeModal.managePaymentMethods.removeDescription", {
            brand: pendingRemoval?.method.brand,
            last4: pendingRemoval?.method.last4,
            defaultValue:
              "This will unlink {brand} ending {last4} from your account. Existing campaigns won't be affected, and this card won't be charged again.",
          })}
        </p>
        <div className="mt-5 grid grid-cols-2 gap-2">
          <button
            type="button"
            className="h-10 rounded-lg border border-[#d8d8d8] font-medium"
            disabled={isRemoving}
            onClick={() => setPendingRemoval(undefined)}
          >
            {t("components.chargeModal.managePaymentMethods.cancel", {
              defaultValue: "Cancel",
            })}
          </button>
          <button
            type="button"
            className="h-10 rounded-lg bg-[#e94f1d] font-medium text-white disabled:cursor-not-allowed disabled:opacity-60"
            disabled={isRemoving}
            onClick={handleRemove}
          >
            {isRemoving
              ? t("components.chargeModal.managePaymentMethods.removing", {
                  defaultValue: "Removing…",
                })
              : t("components.chargeModal.managePaymentMethods.remove", {
                  defaultValue: "Remove",
                })}
          </button>
        </div>
      </Modal>
    </>
  );
}
